import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

interface MonthlyData {
  total_income: string;
  total_expenses: string;
  net_income: string;
}

interface ChartData {
  month: string;
  income: number;
  expenses: number;
  net: number;
}

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip" style={{
        background: 'white',
        padding: '10px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #E5E7EB',
        fontSize: '14px',
        color: '#1F2937'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#111827' }}>{label}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: '10px', height: '10px', backgroundColor: '#10B981', borderRadius: '2px', marginRight: '8px' }}></div>
            <span>Dakhli: </span>
            <span style={{ marginLeft: 'auto', fontWeight: 500 }}>
              KSh {payload[0].value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: '10px', height: '10px', backgroundColor: '#EF4444', borderRadius: '2px', marginRight: '8px' }}></div>
            <span>Kharash: </span>
            <span style={{ marginLeft: 'auto', fontWeight: 500 }}>
              KSh {payload[1].value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #E5E7EB' }}>
            <span style={{ fontWeight: 600 }}>Saafi: </span>
            <span style={{
              marginLeft: 'auto',
              fontWeight: 700,
              color: payload[2].value >= 0 ? '#10B981' : '#EF4444'
            }}>
              KSh {payload[2].value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const Charts: React.FC = () => {
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from cookies
        const token = document.cookie
          .split('; ')
          .find(row => row.startsWith('token='))
          ?.split('=')[1];

        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.get<{ income_and_expenses_by_month: Record<string, MonthlyData> }>(
          '/fees/income/',
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        // Transform data for the chart
        const transformedData = Object.entries(response.data.income_and_expenses_by_month)
          .map(([month, data]) => ({
            month: month.substring(0, 3), // Shorten month name to 3 letters
            income: parseFloat(data.total_income.replace(/,/g, '')),
            expenses: Math.abs(parseFloat(data.total_expenses.replace(/,/g, ''))),
            net: parseFloat(data.net_income.replace(/,/g, ''))
          }))
          .sort((a, b) => {
            // Sort by month in chronological order
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return months.indexOf(a.month) - months.indexOf(b.month);
          });

        setChartData(transformedData);
      } catch (err) {
        console.error('Error fetching chart data:', err);
        setError('Failed to load chart data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        backgroundColor: '#F9FAFB',
        borderRadius: '12px',
        border: '1px solid #E5E7EB'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Soo Kabanaya...</span>
          </div>
          <p style={{ marginTop: '12px', color: '#6B7280' }}>Soo kabanaya xogta maaliyadda...</p>
        </div>
      </div>
    );
  }

  if (error || chartData.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        backgroundColor: '#F9FAFB',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div>
          <i className="fas fa-chart-line" style={{ fontSize: '48px', color: '#9CA3AF', marginBottom: '16px' }}></i>
          <p style={{ color: '#6B7280', margin: '0' }}>{error || 'Xog maaliyadeed lama helin'}</p>
        </div>
      </div>
    );
  }

  // Calculate max value for Y-axis with some padding
  const maxValue = Math.max(
    ...chartData.map(item => Math.max(item.income, item.expenses))
  ) * 1.2; // Add 20% padding

  return (
    <div style={{
      width: '100%',
      height: '400px',
      padding: '0'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: '#10B981',
              borderRadius: '3px',
              marginRight: '6px'
            }}></div>
            <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Dakhli</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: '#EF4444',
              borderRadius: '3px',
              marginRight: '6px'
            }}></div>
            <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Kharash</span>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height="85%">
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
        >
          <defs>
            <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#10B981" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#EF4444" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#EF4444" stopOpacity={0.05} />
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#E5E7EB"
          />

          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6B7280', fontSize: 12 }}
            padding={{ left: 10, right: 10 }}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6B7280', fontSize: 12 }}
            tickFormatter={(value) => `KSh ${(value / 1000)}k`}
            domain={[0, maxValue]}
          />

          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: '#E5E7EB', strokeWidth: 1, strokeDasharray: '4 4' }}
          />

          <ReferenceLine y={0} stroke="#9CA3AF" strokeDasharray="3 3" />

          <Area
            type="monotone"
            dataKey="income"
            name="Income"
            stroke="#10B981"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#incomeGradient)"
            activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
            animationDuration={1500}
          />

          <Area
            type="monotone"
            dataKey="expenses"
            name="Expenses"
            stroke="#EF4444"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#expenseGradient)"
            activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
            animationDuration={1500}
          />

          <Area
            type="monotone"
            dataKey="net"
            name="Net"
            stroke="#3B82F6"
            strokeWidth={0}
            fillOpacity={0}
            activeDot={false}
            legendType="none"
          />
        </AreaChart>
      </ResponsiveContainer>

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '8px'
      }}>
        <span style={{
          fontSize: '0.75rem',
          color: '#9CA3AF',
          fontStyle: 'italic'
        }}>
          Dul imow dhibcaha si aad u aragto faahfaahin
        </span>
      </div>
    </div>
  );
};

export default Charts;
